// frontend/src/pages/Admin/Dashboard.jsx
import { useState, useEffect } from 'react';
import { getAdminDashboard } from '../../api/services';
import {
    UsersIcon,
    CreditCardIcon,
    CurrencyPoundIcon,
    UserGroupIcon,
    ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const { data } = await getAdminDashboard();
            setStats(data);
        } catch (err) {
            setError(err?.response?.data?.detail || 'Failed to fetch dashboard data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <svg className="animate-spin h-10 w-10 text-brand-amber" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                    <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card border-red-500/30 bg-red-500/5 text-red-400 font-body">
                Error: {error}
            </div>
        );
    }

    if (!stats) return null;

    // Subtle multi-colour accents tied to the product's own tier vocabulary.
    const statCards = [
        { title: 'Total Users', value: stats.total_users || 0, icon: UsersIcon, accent: 'text-brand-amber', ring: 'bg-brand-amber/10' },
        { title: 'Active Subscriptions', value: stats.total_subscriptions || 0, icon: UserGroupIcon, accent: 'text-green-400', ring: 'bg-green-500/10' },
        { title: 'PAYG Payments', value: stats.total_payg_payments || 0, icon: CreditCardIcon, accent: 'text-brand-amber', ring: 'bg-brand-amber/10' },
        { title: 'Total Revenue', value: `£${stats.total_revenue || '0.00'}`, icon: CurrencyPoundIcon, accent: 'text-green-400', ring: 'bg-green-500/10' },
        { title: 'Monthly Active Users', value: stats.monthly_active_users || 0, icon: ArrowTrendingUpIcon, accent: 'text-blue-400', ring: 'bg-blue-500/10' },
        { title: "Today's Signups", value: stats.daily_signups || 0, icon: ArrowTrendingUpIcon, accent: 'text-orange-400', ring: 'bg-orange-500/10' },
    ];

    return (
        <div>
            <div className="mb-8">
                <h1 className="font-display text-3xl font-bold text-brand-text">Admin Dashboard</h1>
                <p className="text-brand-muted font-body">Overview of your GMC instance</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {statCards.map((stat, index) => (
                    <div key={index} className="card">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-brand-muted font-body">{stat.title}</p>
                                <p className={`font-display text-2xl font-bold mt-1 ${stat.accent}`}>{stat.value}</p>
                            </div>
                            <div className={`${stat.ring} p-3 rounded-xl`}>
                                <stat.icon className={`h-6 w-6 ${stat.accent}`} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="card">
                <h2 className="font-display text-lg font-semibold text-brand-text mb-4">Recent Users</h2>
                <div className="space-y-1">
                    {(stats.recent_users || []).slice(0, 5).map((user) => (
                        <div
                            key={user.id}
                            className="flex items-center justify-between border-b border-brand-border py-3 last:border-0"
                        >
                            <div>
                                <p className="font-medium text-brand-text font-body">
                                    {user.first_name} {user.last_name || ''}
                                </p>
                                <p className="text-sm text-brand-muted font-body">{user.email}</p>
                            </div>
                            <span className="text-xs text-brand-muted font-body capitalize border border-brand-border px-3 py-1 rounded-full">
                                {user.plan_tier}
                            </span>
                        </div>
                    ))}
                    {(stats.recent_users || []).length === 0 && (
                        <p className="text-brand-muted font-body text-sm py-4 text-center">No users yet.</p>
                    )}
                </div>
            </div>
        </div>
    );
}